import React, { useState, useEffect } from 'react';
import './InitiativeManagement.css';
import '../styles/ResizableTable.css';
import { useUser } from '../contexts/UserContext';
import { initiativeService, Initiative } from '../services/api';
import axios from 'axios';
import { api } from '../config';
import { getDivisions } from '../utils/departments';
import { initResizableTables } from '../utils/resizableTables';
import Spinner from './Spinner';

const InitiativeManagement: React.FC = () => {
  const { user, division } = useUser();
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [action, setAction] = useState<'approve' | 'reject' | 'park' | 'delegate' | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [reviewers, setReviewers] = useState<{ email: string; name: string; user_type: string }[]>([]);
  const [delegates, setDelegates] = useState<{ id: number; email: string; display_name: string | null; is_active: boolean }[]>([]);
  const [selectedReviewerEmail, setSelectedReviewerEmail] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState<{ email: string; name: string; isPrevious?: boolean }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [processedCurrentPage, setProcessedCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState<'all' | 'urgent' | 'normal'>('all');
  const [activeDivision, setActiveDivision] = useState<string | null>(null);

  // Set active division to division on mount
  useEffect(() => {
    if (division && !activeDivision) {
      setActiveDivision(division);
    }
  }, [division, activeDivision]);
  // New state for review request tracking
  const [delegatedInitiatives, setDelegatedInitiatives] = useState<Set<number>>(new Set());
  const [delegationFlow, setDelegationFlow] = useState<any>(null);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [delegateModalHistory, setDelegateModalHistory] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInitiativeId, setDeleteInitiativeId] = useState<number | null>(null);
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);
  const [editingDivision, setEditingDivision] = useState<string>('');
  const [divisionSaving, setDivisionSaving] = useState(false);
  const [dqReviewers, setDqReviewers] = useState<{ email: string; display_name: string | null; division: string | null }[]>([]);
  const [notifyOnApprove, setNotifyOnApprove] = useState(true);
  const [notifyApprovers, setNotifyApprovers] = useState(true);
  const [reviewTab, setReviewTab] = useState<'pending' | 'approved' | 'rejected' | 'parked'>('pending');
  const [sortConfig, setSortConfig] = useState<Record<string, { column: string; direction: 'asc' | 'desc' }>>({
    pending: { column: 'submitted_date', direction: 'desc' },
    approved: { column: 'approval_date', direction: 'desc' },
    rejected: { column: 'approval_date', direction: 'desc' },
    parked: { column: 'approval_date', direction: 'desc' },
  });
  const itemsPerPage = 10;

  useEffect(() => {
    loadInitiatives();
    loadReviewers();
    loadDelegates();
    axios.get(api('/api/dq-reviewers/list')).then(r => setDqReviewers(r.data)).catch(() => {});
  }, []);

  // Initialize resizable tables when initiatives load
  useEffect(() => {
    const timers = [
      setTimeout(() => initResizableTables(), 50),
      setTimeout(() => initResizableTables(), 200),
    ];
    return () => timers.forEach(t => clearTimeout(t));
  }, [initiatives.length]);

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showWorkflowModal && !workflowLoading) {
          setShowWorkflowModal(false);
          setDelegationFlow(null);
        } else if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
          setDeleteInitiativeId(null);
        } else if (action) {
          setAction(null);
          setComment('');
          setSelectedReviewerEmail('');
        } else if (selectedInitiative) {
          setSelectedInitiative(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [selectedInitiative, action, showWorkflowModal, workflowLoading, showDeleteConfirm]);

  const loadReviewers = async () => {
    try {
      const response = await axios.get(api('/api/users/list/for-delegation'));
      setReviewers(response.data);
    } catch (error) {
      console.error('Error loading reviewers:', error);
    }
  };

  const loadDelegates = async () => {
    try {
      const response = await axios.get(api('/api/delegates/list'));
      setDelegates(response.data);
    } catch (error) {
      console.error('Error loading delegates:', error);
    }
  };

  const loadReviewRequests = async () => {
    try {
      // Fetch all initiatives that have been delegated (have ReviewRequests)
      const response = await axios.get(api(`/api/reviews/delegated-initiatives`));
      const delegatedIds = new Set<number>(response.data?.delegated_initiative_ids || []);
      setDelegatedInitiatives(delegatedIds);
      console.log(`Loaded ${delegatedIds.size} delegated initiatives`);
    } catch (error) {
      console.error('Error loading delegated initiatives:', error);
    }
  };

  const fetchDelegationFlow = async (initiativeId: number) => {
    setWorkflowLoading(true);
    setShowWorkflowModal(true);
    try {
      const response = await axios.get(
        api(`/api/reviews/flow/${initiativeId}`)
      );
      console.log(`[WORKFLOW] Fetched delegation flow for initiative ${initiativeId}:`, response.data);
      console.log(`[WORKFLOW] Total steps in flow: ${response.data.flow?.length || 0}`);
      response.data.flow?.forEach((step: any, index: number) => {
        console.log(`[WORKFLOW] Step ${index + 1}: ${step.from_name} → ${step.to_name} (order: ${step.step})`);
      });
      setDelegationFlow(response.data);
    } catch (error) {
      console.error('Error fetching delegation flow:', error);
      setMessage({ type: 'error', text: 'Failed to load workflow details' });
      setShowWorkflowModal(false);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleReviewerEmailChange = (email: string) => {
    setSelectedReviewerEmail(email);
    
    // Validate email format if email is provided
    if (email.trim() && !isValidEmail(email)) {
      setEmailValidationError('Please enter a valid email address');
    } else {
      setEmailValidationError(null);
    }

    if (!email.trim()) {
      // Show all active delegates when input is empty
      const suggestions = delegates
        .filter(d => d.is_active)
        .map(delegate => ({
          email: delegate.email,
          name: delegate.display_name || delegate.email
        }));
      setFilteredSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      return;
    }

    // Filter delegates by matching email or name
    const lowerEmail = email.toLowerCase();
    const filtered = delegates
      .filter(d => d.is_active && (
        d.email.toLowerCase().includes(lowerEmail) ||
        (d.display_name && d.display_name.toLowerCase().includes(lowerEmail))
      ))
      .map(delegate => ({
        email: delegate.email,
        name: delegate.display_name || delegate.email
      }));

    setFilteredSuggestions(filtered);
    setShowSuggestions(filtered.length > 0 || email.includes('@'));
  };

  const selectReviewerFromSuggestion = (email: string, name?: string) => {
    setSelectedReviewerEmail(email);
    setFilteredSuggestions([]);
    setShowSuggestions(false);
    setEmailValidationError(null);
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateEmail = (email: string): boolean => {
    if (!email.trim()) {
      setEmailValidationError('Email is required');
      return false;
    }
    if (!isValidEmail(email)) {
      setEmailValidationError('Please enter a valid email address');
      return false;
    }
    setEmailValidationError(null);
    return true;
  };

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedInitiative(null);
        setComment('');
        setAction(null);
        setShowWorkflowModal(false);
      }
    };

    if (selectedInitiative || showWorkflowModal) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [selectedInitiative, showWorkflowModal]);

  // Fetch delegation history when opening delegate modal
  useEffect(() => {
    if (action === 'delegate' && selectedInitiative) {
      const fetchHistory = async () => {
        try {
          const response = await axios.get(
            api(`/api/reviews/flow/${selectedInitiative.id}`)
          );
          setDelegateModalHistory(response.data);
        } catch (error) {
          console.error('Error fetching delegation history:', error);
          setDelegateModalHistory(null);
        }
      };
      fetchHistory();
    } else {
      setDelegateModalHistory(null);
    }
  }, [action, selectedInitiative]);

  const loadInitiatives = async () => {
    setIsLoading(true);
    try {
      const response = await initiativeService.getInitiatives();
      let initiatives = response.data || [];
      
      // Show all initiatives by default (role-based filtering disabled)
      // if (!isReviewer && user?.email) {
      //   try {
      //     const reviewQueueResponse = await axios.get(
      //       api(`/api/reviews/queue/${encodeURIComponent(user.email)}`)
      //     );
      //     const reviewedInitiativeIds = new Set(
      //       (reviewQueueResponse.data || [])
      //         .filter((r: any) => r.is_routed_to_current_user)
      //         .map((r: any) => r.initiative_id)
      //     );
      //     initiatives = initiatives.filter((init: Initiative) => 
      //       reviewedInitiativeIds.has(init.id)
      //     );
      //   } catch (error) {
      //     console.error('Error filtering initiatives for reviewer:', error);
      //     initiatives = [];
      //   }
      // }
      
      // Sort by status (pending first), then by submitted_date descending
      const sorted = initiatives.sort((a, b) => {
        if (a.status === 'pending' && b.status !== 'pending') return -1;
        if (a.status !== 'pending' && b.status === 'pending') return 1;
        return new Date(b.submitted_date).getTime() - new Date(a.submitted_date).getTime();
      });
      setInitiatives(sorted);
      // Load review requests to determine which initiatives are delegated
      await loadReviewRequests();
      // Ensure spinner shows for at least 300ms for better UX
      await new Promise(resolve => setTimeout(resolve, 300));
    } catch (error) {
      console.error('Error loading initiatives:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateDaysSinceSubmission = (submittedDate: string): number => {
    const submitted = new Date(submittedDate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - submitted.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateDaysRemaining = (submittedDate: string): number => {
    const submitted = new Date(submittedDate);
    const oneWeekLater = new Date(submitted.getTime() + 7 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffTime = oneWeekLater.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const calculateDueDate = (submittedDate: string): string => {
    const submitted = new Date(submittedDate);
    const dueDate = new Date(submitted.getTime() + 7 * 24 * 60 * 60 * 1000);
    return formatDate(dueDate.toISOString());
  };

  const isSLABreached = (submittedDate: string): boolean => {
    const daysSince = calculateDaysSinceSubmission(submittedDate);
    return daysSince > 7;
  };

  const isResponseWithinSLA = (submittedDate: string, responseDate?: string): boolean => {
    if (!responseDate) return false;
    const submitted = new Date(submittedDate);
    const response = new Date(responseDate);
    const diffTime = response.getTime() - submitted.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const openInitiative = (initiative: Initiative) => {
    setSelectedInitiative(initiative);
    setEditingDivision(initiative.division || '');
  };

  const saveDivision = async (initiativeId: number, division: string) => {
    setDivisionSaving(true);
    try {
      await axios.patch(api(`/api/initiatives/${initiativeId}/division`), { division });
      // Update local state
      setInitiatives(prev => prev.map(i => i.id === initiativeId ? { ...i, division: division || undefined } : i));
      if (selectedInitiative?.id === initiativeId) {
        setSelectedInitiative(prev => prev ? { ...prev, division: division || undefined } : prev);
      }
    } catch {}
    finally { setDivisionSaving(false); }
  };

  const handleApprove = async () => {
    if (!selectedInitiative) return;

    if (!comment.trim()) {
      setMessage({ type: 'error', text: 'Comment is required for approval' });
      return;
    }

    setIsSubmitting(true);
    try {
      await initiativeService.approveInitiative(selectedInitiative.id, comment, user?.email, notifyOnApprove, notifyApprovers);
      setMessage({ type: 'success', text: 'Initiative approved successfully' });
      setTimeout(() => {
        setSelectedInitiative(null);
        setComment('');
        setAction(null);
        setNotifyOnApprove(true);
        setNotifyApprovers(true);
        setShowWorkflowModal(false);
        setDelegationFlow(null);
        setMessage(null);
        loadInitiatives();
      }, 1500);
    } catch (error: any) {
      console.error('Error approving initiative:', error);
      setMessage({ type: 'error', text: 'Failed to approve initiative' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedInitiative) return;

    if (!comment.trim()) {
      setMessage({ type: 'error', text: 'Comment is required for rejection' });
      return;
    }

    setIsSubmitting(true);
    try {
      await initiativeService.rejectInitiative(selectedInitiative.id, comment, user?.email);
      setMessage({ type: 'success', text: 'Initiative rejected successfully' });
      setTimeout(() => {
        setSelectedInitiative(null);
        setComment('');
        setAction(null);
        setShowWorkflowModal(false);
        setDelegationFlow(null);
        setMessage(null);
        loadInitiatives();
      }, 1500);
    } catch (error: any) {
      console.error('Error rejecting initiative:', error);
      setMessage({ type: 'error', text: 'Failed to reject initiative' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePark = async () => {
    if (!selectedInitiative) return;
    if (!comment.trim()) {
      setMessage({ type: 'error', text: 'Comment is required for parking' });
      return;
    }
    setIsSubmitting(true);
    try {
      await initiativeService.parkInitiative(selectedInitiative.id, comment, user?.email);
      setMessage({ type: 'success', text: 'Initiative parked successfully' });
      setTimeout(() => {
        setSelectedInitiative(null);
        setComment('');
        setAction(null);
        setShowWorkflowModal(false);
        setDelegationFlow(null);
        setMessage(null);
        loadInitiatives();
      }, 1500);
    } catch (error: any) {
      console.error('Error parking initiative:', error);
      setMessage({ type: 'error', text: 'Failed to park initiative' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelegate = async () => {
    if (!selectedInitiative || !selectedReviewerEmail) {
      setEmailValidationError('Please select a reviewer');
      return;
    }

    if (!validateEmail(selectedReviewerEmail)) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Check if the delegate email exists in delegates table, if not add it
      const delegateExists = delegates.find(d => d.email === selectedReviewerEmail.toLowerCase());
      if (!delegateExists) {
        // Add new delegate if it doesn't exist
        try {
          await axios.post(api('/api/delegates/add'), {}, {
            params: { 
              email: selectedReviewerEmail.toLowerCase(),
              requester_email: user?.email || ''
            }
          });
          // Reload delegates after adding new one
          await loadDelegates();
        } catch (addError: any) {
          console.warn('Could not add delegate to table, proceeding with delegation:', addError);
        }
      }

      // Proceed with delegation
      await axios.post(api('/api/reviews/delegate'), null, {
        params: {
          initiative_id: selectedInitiative.id,
          delegated_from_email: user?.email || '',
          delegated_to_email: selectedReviewerEmail,
          review_comment: comment || undefined,
        },
      });
      setMessage({ type: 'success', text: 'Initiative delegated successfully' });
      setTimeout(() => {
        setSelectedInitiative(null);
        setAction(null);
        setComment('');
        setNotifyOnApprove(true);
        setNotifyApprovers(true);
        setSelectedReviewerEmail('');
        setShowWorkflowModal(false);
        setDelegationFlow(null);
        setMessage(null);
        loadInitiatives();
      }, 1500);
    } catch (error: any) {
      console.error('Error delegating initiative:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delegate initiative';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (initiativeId: number) => {
    setDeleteInitiativeId(initiativeId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteInitiativeId) return;

    setIsSubmitting(true);
    try {
      await initiativeService.deleteInitiative(deleteInitiativeId);
      setMessage({ type: 'success', text: 'Initiative deleted successfully' });
      setShowDeleteConfirm(false);
      setDeleteInitiativeId(null);
      if (selectedInitiative?.id === deleteInitiativeId) {
        setSelectedInitiative(null);
      }
      setTimeout(() => {
        loadInitiatives();
      }, 500);
    } catch (error: any) {
      console.error('Error deleting initiative:', error);
      setMessage({ type: 'error', text: 'Failed to delete initiative' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendNotification = async () => {
    if (!selectedInitiative) return;

    setIsSubmitting(true);
    try {
      await axios.post(api(`/api/reviews/send-notification/${selectedInitiative.id}`), null, {
        params: {
          reviewer_email: user?.email || '',
        },
      });
      setMessage({ type: 'success', text: 'Notification email resent successfully' });
    } catch (error: any) {
      console.error('Error resending notification:', error);
      setMessage({ type: 'error', text: 'Failed to resend notification email' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to format date and time
  const formatDateTime = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    
    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths}mo ago`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateWithTime = (dateString: string) => {
    // Parse the date string - browser will interpret based on locale
    const date = new Date(dateString);
    
    // Format to local timezone date and time
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'status-pending';
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      case 'parked':
        return 'status-parked';
      default:
        return 'status-pending';
    }
  };

  const getSLAUrgencyClass = (daysRemaining: number, status: string) => {
    if (status !== 'pending') return '';
    if (daysRemaining <= 2) return 'urgency-critical';
    if (daysRemaining <= 4) return 'urgency-warning';
    return 'urgency-normal';
  };

  const getPaginatedItems = (items: Initiative[], currentPage: number) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items: Initiative[]) => {
    return Math.ceil(items.length / itemsPerPage);
  };

  const getFilteredInitiatives = (items: Initiative[], query: string, urgency: string) => {
    let filtered = items;

    // Search by initiative name
    if (query.trim()) {
      filtered = filtered.filter(i =>
        i.use_case_name.toLowerCase().includes(query.toLowerCase()) ||
        i.submitted_by.toLowerCase().includes(query.toLowerCase())
      );
    }

    // Filter by urgency (only for pending)
    if (urgency === 'urgent') {
      filtered = filtered.filter(i => calculateDaysRemaining(i.submitted_date) <= 2);
    }

    return filtered;
  };

  const handleSort = (tab: string, column: string) => {
    setSortConfig(prev => {
      const current = prev[tab];
      const direction = current?.column === column && current.direction === 'asc' ? 'desc' : 'asc';
      return { ...prev, [tab]: { column, direction } };
    });
  };

  const sortIcon = (tab: string, column: string) => {
    const cfg = sortConfig[tab];
    if (!cfg || cfg.column !== column) return '\u21c5';
    return cfg.direction === 'asc' ? '\u25b2' : '\u25bc';
  };

  const sortItems = (items: Initiative[], tab: string): Initiative[] => {
    const cfg = sortConfig[tab];
    if (!cfg) return items;
    const arr = [...items];
    arr.sort((a: any, b: any) => {
      let result = 0;
      const valA = a[cfg.column];
      const valB = b[cfg.column];
      if (cfg.column.toLowerCase().includes('date')) {
        result = new Date(valA || 0).getTime() - new Date(valB || 0).getTime();
      } else if (valA == null && valB == null) result = 0;
      else if (valA == null) result = -1;
      else if (valB == null) result = 1;
      else if (typeof valA === 'number' && typeof valB === 'number') result = valA - valB;
      else result = String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
      return cfg.direction === 'asc' ? result : -result;
    });
    return arr;
  };

  // Helper function to get request status badge
  const getRequestStatusBadge = (initiativeId: number) => {
    const isDelegated = delegatedInitiatives.has(initiativeId);
    
    if (isDelegated) {
      return (
        <span className="status-badge delegated" title="Delegated - review is in progress">
          📤 DELEGATED
        </span>
      );
    }
    
    return (
      <span className="status-badge new-request" title="New submission - awaiting assignment to reviewer">
        🆕 NEW REQUEST
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="initiative-mgmt">
        <Spinner message="Fetching initiatives from database..." />
      </div>
    );
  }

  const pendingInitiatives = initiatives.filter(i => i.status === 'pending');
  const processedInitiatives = initiatives.filter(i => i.status !== 'pending');

  // Get all divisions from database (always show all, even if empty)
  const allDivisions = getDivisions();

  // Division sub-tabs for pending
  const pendingDivisions = allDivisions;
  const pendingByDivision = activeDivision === 'all' 
    ? pendingInitiatives
    : activeDivision ? pendingInitiatives.filter(i => (i.division || 'Unassigned') === activeDivision) : [];

  // Division sub-tabs for other tabs
  const approvedInitiatives = initiatives.filter(i => i.status === 'approved');
  const approvedDivisions = allDivisions;
  const approvedByDivision = activeDivision === 'all'
    ? approvedInitiatives
    : approvedInitiatives.filter(i => (i.division || 'Unassigned') === activeDivision);

  const rejectedInitiatives = initiatives.filter(i => i.status === 'rejected');
  const rejectedDivisions = allDivisions;
  const rejectedByDivision = activeDivision === 'all'
    ? rejectedInitiatives
    : rejectedInitiatives.filter(i => (i.division || 'Unassigned') === activeDivision);

  const parkedInitiatives = initiatives.filter(i => i.status === 'parked');
  const parkedDivisions = allDivisions;
  const parkedByDivision = activeDivision === 'all'
    ? parkedInitiatives
    : parkedInitiatives.filter(i => (i.division || 'Unassigned') === activeDivision);

  const pendingCount = pendingInitiatives.length;
  const approvedCount = approvedInitiatives.length;
  const rejectedCount = rejectedInitiatives.length;
  const parkedCount = parkedInitiatives.length;
  const urgentCount = pendingInitiatives.filter(i => calculateDaysRemaining(i.submitted_date) <= 2).length;

  return (
    <div className="initiative-mgmt">
      {/* Stats Cards */}
      <div className="mgmt-stats">
            <div className="stat-card">
              <div className="stat-number">{pendingCount}</div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{approvedCount}</div>
              <div className="stat-label">Approved</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{rejectedCount}</div>
              <div className="stat-label">Rejected</div>
            </div>
            <div className="stat-card stat-parked">
              <div className="stat-number">{parkedCount}</div>
              <div className="stat-label">Parked</div>
            </div>
            {urgentCount > 0 && (
              <div className="stat-card urgent">
                <div className="stat-number">{urgentCount}</div>
                <div className="stat-label">Urgent (≤2 days)</div>
              </div>
            )}
          </div>

          {initiatives.length === 0 ? (
            <div className="empty-state">
              <p>No initiatives to manage</p>
            </div>
          ) : (
            <>
              {/* Division Pills */}
          {(() => {
            let divisionsToShow: string[] = [];
            let initiativesToCount = pendingInitiatives;
            if (reviewTab === 'pending') {
              divisionsToShow = pendingDivisions;
              initiativesToCount = pendingInitiatives;
            } else if (reviewTab === 'approved') {
              divisionsToShow = approvedDivisions;
              initiativesToCount = approvedInitiatives;
            } else if (reviewTab === 'rejected') {
              divisionsToShow = rejectedDivisions;
              initiativesToCount = rejectedInitiatives;
            } else if (reviewTab === 'parked') {
              divisionsToShow = parkedDivisions;
              initiativesToCount = parkedInitiatives;
            }
            
            return divisionsToShow.length > 0 && (
              <div className="division-subtabs">
                {divisionsToShow.map(div => (
                  <button
                    key={div}
                    className={`division-subtab ${activeDivision === div ? 'division-subtab--active' : ''}`}
                    onClick={() => setActiveDivision(div)}
                  >
                    {div} <span className="division-subtab__count">
                      {isLoading ? '...' : initiatives.filter(i => (i.division || 'Unassigned') === div).length}
                    </span>
                  </button>
                ))}
              </div>
            );
          })()}

          {/* Review Status Tabs */}
          <div className="status-tabs" style={{ display: 'flex', gap: '0', marginBottom: '16px', flexWrap: 'wrap', paddingBottom: '0', borderBottom: '2px solid #e8e8e8' }}>
            <button
              className={`status-tab ${reviewTab === 'pending' ? 'status-tab--active' : ''}`}
              onClick={() => { setReviewTab('pending'); setPendingCurrentPage(1); setProcessedCurrentPage(1); }}
            >
              Pending
            </button>
            <button
              className={`status-tab ${reviewTab === 'approved' ? 'status-tab--active' : ''}`}
              onClick={() => { setReviewTab('approved'); setProcessedCurrentPage(1); }}
            >
              Approved
            </button>
            <button
              className={`status-tab ${reviewTab === 'rejected' ? 'status-tab--active' : ''}`}
              onClick={() => { setReviewTab('rejected'); setProcessedCurrentPage(1); }}
            >
              Rejected
            </button>
            <button
              className={`status-tab ${reviewTab === 'parked' ? 'status-tab--active' : ''}`}
              onClick={() => { setReviewTab('parked'); setProcessedCurrentPage(1); }}
            >
              Parked
            </button>
          </div>

          {/* Filter Controls & Content - Show only for Pending tab */}
          {reviewTab === 'pending' && (
            <>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px', marginBottom: '20px', marginTop: 0, flexWrap: 'nowrap', width: '100%' }}>
            <input
              type="text"
              placeholder="Search by initiative name or submitter..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPendingCurrentPage(1);
                setProcessedCurrentPage(1);
              }}
              style={{ flex: 1, padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', minWidth: 0 }}
            />
            <label htmlFor="urgency-filter" style={{ whiteSpace: 'nowrap', fontSize: '14px', fontWeight: 500, color: '#333' }}>Filter by Urgency:</label>
            <select
              id="urgency-filter"
              value={urgencyFilter}
              onChange={(e) => {
                setUrgencyFilter(e.target.value as 'all' | 'urgent' | 'normal');
                setPendingCurrentPage(1);
              }}
              style={{ padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', flexShrink: 0 }}
            >
              <option value="all">All Requests</option>
              <option value="urgent">Urgent (≤2 days)</option>
              <option value="normal">Normal (&gt;2 days)</option>
            </select>
          </div>

          {/* Requests Content */}
          {pendingByDivision.length > 0 && (
            <div className="initiatives-section">
              <div className="initiatives-table-wrapper">
                <table id="review-pending-table" className="initiatives-table">
                  <thead>
                    <tr>
                      <th className="sortable-th" onClick={() => handleSort('pending', 'id')}>ID {sortIcon('pending', 'id')}</th>
                      <th className="sortable-th" onClick={() => handleSort('pending', 'use_case_name')}>Initiative Name {sortIcon('pending', 'use_case_name')}</th>
                      <th className="sortable-th" onClick={() => handleSort('pending', 'division')}>Division {sortIcon('pending', 'division')}</th>
                      <th className="sortable-th" onClick={() => handleSort('pending', 'submitted_by')}>Submitted By {sortIcon('pending', 'submitted_by')}</th>
                      <th className="sortable-th" onClick={() => handleSort('pending', 'proposed_solution_owner')}>Owner {sortIcon('pending', 'proposed_solution_owner')}</th>
                      <th>Status</th>
                      <th className="sortable-th" onClick={() => handleSort('pending', 'submitted_date')}>Submitted Date {sortIcon('pending', 'submitted_date')}</th>
                      <th>Days Since</th>
                      <th>Days Remaining (SLA: 7 days)</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedItems(
                      sortItems(getFilteredInitiatives(pendingByDivision, searchQuery, urgencyFilter), 'pending'),
                      pendingCurrentPage
                    ).map((initiative) => {
                      const daysRemaining = calculateDaysRemaining(initiative.submitted_date);
                      const daysSince = calculateDaysSinceSubmission(initiative.submitted_date);
                      const slaBreached = isSLABreached(initiative.submitted_date);
                      return (
                        <tr key={initiative.id} className={`pending-row ${getSLAUrgencyClass(daysRemaining, initiative.status)}`}>
                          <td className="id-cell">#{initiative.id}</td>
                          <td className="name-cell">{initiative.use_case_name}</td>
                          <td style={{ minWidth: '140px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <select
                              className="division-inline-select"
                              value={initiative.division || ''}
                              onChange={(e) => saveDivision(initiative.id, e.target.value)}
                              disabled={divisionSaving || (initiative.division || 'Unassigned') !== division}
                              style={{ flex: 1 }}
                            >
                              <option value="">— Not set —</option>
                              {Array.from(new Set([
                                ...getDivisions(),
                                ...(initiative.division ? [initiative.division] : [])
                              ])).sort().map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                            {divisionSaving && <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>⏳</span>}
                          </td>
                          <td>{initiative.submitted_by}</td>
                          <td>{initiative.proposed_solution_owner}</td>
                          <td>{getRequestStatusBadge(initiative.id)}</td>
                          <td>{formatDate(initiative.submitted_date)}</td>
                          <td className="days-cell">{daysSince} days</td>
                          <td className="sla-cell">
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                              {slaBreached ? (
                                <span className="sla-badge breach">BREACHED SLA</span>
                              ) : (
                                <span className={`sla-badge ${daysRemaining <= 2 ? 'critical' : daysRemaining <= 4 ? 'warning' : 'normal'}`}>
                                  {daysRemaining} days
                                </span>
                              )}
                              {daysRemaining <= 2 && !slaBreached && <span className="urgent-indicator">⚠ URGENT</span>}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                              <button
                                className="btn-icon-review"
                                onClick={() => openInitiative(initiative)}
                                title="Review initiative"
                              >
                                <img src="/reviewinitiative.png" alt="Review initiative" />
                              </button>
                              <button
                                className="btn-icon-workflow"
                                onClick={() => fetchDelegationFlow(initiative.id)}
                                title="View workflow"
                              >
                                <img src="/viewworkflow.png" alt="View workflow" />
                              </button>
                              <button
                                className="btn-icon-delete"
                                onClick={() => handleDelete(initiative.id)}
                                title="Delete initiative"
                              >
                                <img src="/deletesubmission.png" alt="Delete initiative" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {getTotalPages(getFilteredInitiatives(pendingByDivision, searchQuery, urgencyFilter)) > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => setPendingCurrentPage(Math.max(1, pendingCurrentPage - 1))}
                    disabled={pendingCurrentPage === 1}
                  >
                    ← Previous
                  </button>
                  <div className="pagination-info">
                    Page {pendingCurrentPage} of {getTotalPages(getFilteredInitiatives(pendingByDivision, searchQuery, urgencyFilter))}
                  </div>
                  <button
                    className="pagination-btn"
                    onClick={() => setPendingCurrentPage(Math.min(getTotalPages(getFilteredInitiatives(pendingByDivision, searchQuery, urgencyFilter)), pendingCurrentPage + 1))}
                    disabled={pendingCurrentPage === getTotalPages(getFilteredInitiatives(pendingByDivision, searchQuery, urgencyFilter))}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}

          {pendingByDivision.length === 0 && (
            <div className="empty-state">
              <p>No requests{activeDivision !== 'all' ? ` for ${activeDivision}` : ''}</p>
            </div>
          )}
            </>
          )}

          {/* Approved Initiatives */}
          {reviewTab === 'approved' && (
            <>
              {approvedByDivision.length > 0 ? (
                <div className="initiatives-section">
                  <div className="initiatives-table-wrapper">
                    <table id="review-approved-table" className="initiatives-table">
                      <thead>
                        <tr>
                          <th className="sortable-th" onClick={() => handleSort('approved', 'id')}>ID {sortIcon('approved', 'id')}</th>
                          <th className="sortable-th" onClick={() => handleSort('approved', 'use_case_name')}>Initiative Name {sortIcon('approved', 'use_case_name')}</th>
                          <th className="sortable-th" onClick={() => handleSort('approved', 'division')}>Division {sortIcon('approved', 'division')}</th>
                          <th className="sortable-th" onClick={() => handleSort('approved', 'submitted_by')}>Submitted By {sortIcon('approved', 'submitted_by')}</th>
                          <th className="sortable-th" onClick={() => handleSort('approved', 'proposed_solution_owner')}>Owner {sortIcon('approved', 'proposed_solution_owner')}</th>
                          <th className="sortable-th" onClick={() => handleSort('approved', 'submitted_date')}>Submitted Date {sortIcon('approved', 'submitted_date')}</th>
                          <th className="sortable-th" onClick={() => handleSort('approved', 'approval_date')}>Approved Date {sortIcon('approved', 'approval_date')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortItems(approvedByDivision, 'approved').map((initiative) => (
                          <tr key={initiative.id} className="approved-row">
                            <td className="id-cell">#{initiative.id}</td>
                            <td className="name-cell">{initiative.use_case_name}</td>
                            <td style={{ minWidth: '140px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <select
                                className="division-inline-select"
                                value={initiative.division || ''}
                                onChange={(e) => saveDivision(initiative.id, e.target.value)}
                                disabled={divisionSaving}
                                style={{ flex: 1 }}
                              >
                                <option value="">— Not set —</option>
                                {Array.from(new Set([
                                  ...getDivisions(),
                                  ...(initiative.division ? [initiative.division] : [])
                                ])).sort().map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                              {divisionSaving && <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>⏳</span>}
                            </td>
                            <td>{initiative.submitted_by}</td>
                            <td>{initiative.proposed_solution_owner}</td>
                            <td>{formatDate(initiative.submitted_date)}</td>
                            <td>{initiative.approval_date ? formatDate(initiative.approval_date) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No approved initiatives{activeDivision !== 'all' ? ` for ${activeDivision}` : ''}</p>
                </div>
              )}
            </>
          )}

          {/* Rejected Initiatives */}
          {reviewTab === 'rejected' && (
            <>
              {rejectedByDivision.length > 0 ? (
                <div className="initiatives-section">
                  <div className="initiatives-table-wrapper">
                    <table id="review-rejected-table" className="initiatives-table">
                      <thead>
                        <tr>
                          <th className="sortable-th" onClick={() => handleSort('rejected', 'id')}>ID {sortIcon('rejected', 'id')}</th>
                          <th className="sortable-th" onClick={() => handleSort('rejected', 'use_case_name')}>Initiative Name {sortIcon('rejected', 'use_case_name')}</th>
                          <th className="sortable-th" onClick={() => handleSort('rejected', 'division')}>Division {sortIcon('rejected', 'division')}</th>
                          <th className="sortable-th" onClick={() => handleSort('rejected', 'submitted_by')}>Submitted By {sortIcon('rejected', 'submitted_by')}</th>
                          <th className="sortable-th" onClick={() => handleSort('rejected', 'proposed_solution_owner')}>Owner {sortIcon('rejected', 'proposed_solution_owner')}</th>
                          <th className="sortable-th" onClick={() => handleSort('rejected', 'submitted_date')}>Submitted Date {sortIcon('rejected', 'submitted_date')}</th>
                          <th className="sortable-th" onClick={() => handleSort('rejected', 'approval_date')}>Rejected Date {sortIcon('rejected', 'approval_date')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortItems(rejectedByDivision, 'rejected').map((initiative) => (
                          <tr key={initiative.id} className="rejected-row">
                            <td className="id-cell">#{initiative.id}</td>
                            <td className="name-cell">{initiative.use_case_name}</td>
                            <td style={{ minWidth: '140px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <select
                                className="division-inline-select"
                                value={initiative.division || ''}
                                onChange={(e) => saveDivision(initiative.id, e.target.value)}
                                disabled={divisionSaving}
                                style={{ flex: 1 }}
                              >
                                <option value="">— Not set —</option>
                                {Array.from(new Set([
                                  ...getDivisions(),
                                  ...(initiative.division ? [initiative.division] : [])
                                ])).sort().map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                              {divisionSaving && <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>⏳</span>}
                            </td>
                            <td>{initiative.submitted_by}</td>
                            <td>{initiative.proposed_solution_owner}</td>
                            <td>{formatDate(initiative.submitted_date)}</td>
                            <td>{initiative.approval_date ? formatDate(initiative.approval_date) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No rejected initiatives{activeDivision !== 'all' ? ` for ${activeDivision}` : ''}</p>
                </div>
              )}
            </>
          )}

          {/* Parked Initiatives */}
          {reviewTab === 'parked' && (
            <>
              {parkedByDivision.length > 0 ? (
                <div className="initiatives-section">
                  <div className="initiatives-table-wrapper">
                    <table id="review-parked-table" className="initiatives-table">
                      <thead>
                        <tr>
                          <th className="sortable-th" onClick={() => handleSort('parked', 'id')}>ID {sortIcon('parked', 'id')}</th>
                          <th className="sortable-th" onClick={() => handleSort('parked', 'use_case_name')}>Initiative Name {sortIcon('parked', 'use_case_name')}</th>
                          <th className="sortable-th" onClick={() => handleSort('parked', 'division')}>Division {sortIcon('parked', 'division')}</th>
                          <th className="sortable-th" onClick={() => handleSort('parked', 'submitted_by')}>Submitted By {sortIcon('parked', 'submitted_by')}</th>
                          <th className="sortable-th" onClick={() => handleSort('parked', 'proposed_solution_owner')}>Owner {sortIcon('parked', 'proposed_solution_owner')}</th>
                          <th className="sortable-th" onClick={() => handleSort('parked', 'submitted_date')}>Submitted Date {sortIcon('parked', 'submitted_date')}</th>
                          <th className="sortable-th" onClick={() => handleSort('parked', 'approval_date')}>Parked Date {sortIcon('parked', 'approval_date')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortItems(parkedByDivision, 'parked').map((initiative) => (
                          <tr key={initiative.id} className="parked-row">
                            <td className="id-cell">#{initiative.id}</td>
                            <td className="name-cell">{initiative.use_case_name}</td>
                            <td style={{ minWidth: '140px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <select
                                className="division-inline-select"
                                value={initiative.division || ''}
                                onChange={(e) => saveDivision(initiative.id, e.target.value)}
                                disabled={divisionSaving}
                                style={{ flex: 1 }}
                              >
                                <option value="">— Not set —</option>
                                {Array.from(new Set([
                                  ...getDivisions(),
                                  ...(initiative.division ? [initiative.division] : [])
                                ])).sort().map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                              {divisionSaving && <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>⏳</span>}
                            </td>
                            <td>{initiative.submitted_by}</td>
                            <td>{initiative.proposed_solution_owner}</td>
                            <td>{formatDate(initiative.submitted_date)}</td>
                            <td>{initiative.approval_date ? formatDate(initiative.approval_date) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No parked initiatives{activeDivision !== 'all' ? ` for ${activeDivision}` : ''}</p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Modal for Review/Approve/Reject */}
      {selectedInitiative && (
        <div className="modal-overlay" onClick={() => setSelectedInitiative(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{selectedInitiative.use_case_name}</h3>
                <p className="initiative-id">ID: {selectedInitiative.id}</p>
              </div>
              <button className="modal-close" onClick={() => setSelectedInitiative(null)}>×</button>
            </div>

            <div className="modal-body">
              {message && (
                <div className={`message-alert ${message.type}`}>
                  {message.text}
                </div>
              )}

              <div className="detail-section">
                <div className="detail-row">
                  <span className="label">Use Case Name:</span>
                  <span className="value">{selectedInitiative.use_case_name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Submitted By:</span>
                  <span className="value">{selectedInitiative.submitted_by}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Submitted Date:</span>
                  <span className="value">{formatDate(selectedInitiative.submitted_date)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Days Remaining:</span>
                  {selectedInitiative.status === 'parked' ? (
                    <span className="value sla-highlight" style={{ color: '#7B68EE' }}>
                      N/A (Parked on day {Math.abs(calculateDaysRemaining(selectedInitiative.submitted_date) - 7) + 1})
                    </span>
                  ) : (
                    <span className={`value sla-highlight ${calculateDaysRemaining(selectedInitiative.submitted_date) <= 2 ? 'critical' : ''}`}>
                      {calculateDaysRemaining(selectedInitiative.submitted_date)} days
                    </span>
                  )}
                </div>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  <span className={`value status-badge ${getStatusBadgeClass(selectedInitiative.status)}`}>
                    {selectedInitiative.status.toUpperCase()}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Proposed Owner:</span>
                  <span className="value">{selectedInitiative.proposed_solution_owner}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Division:</span>
                  <span className="value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <select
                      className="division-inline-select"
                      value={editingDivision}
                      onChange={e => {
                        setEditingDivision(e.target.value);
                        saveDivision(selectedInitiative.id, e.target.value);
                      }}
                      disabled={divisionSaving || (selectedInitiative.division || 'Unassigned') !== division}
                    >
                      <option value="">— Not set —</option>
                      {Array.from(new Set([
                        ...getDivisions(),
                        ...(editingDivision ? [editingDivision] : [])
                      ])).sort().map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    {divisionSaving && <span style={{ fontSize: 12, color: '#888' }}>Saving...</span>}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Solution Sponsor:</span>
                  <span className="value">{selectedInitiative.solution_sponsor}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Purpose:</span>
                  <span className="value formatted-text">{selectedInitiative.purpose}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Value/Benefits:</span>
                  <span className="value formatted-text">{selectedInitiative.value}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Users Affected:</span>
                  <span className="value">{selectedInitiative.users}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Solution Scope:</span>
                  <span className="value formatted-text">{selectedInitiative.solution_scope}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Proposed Workflow:</span>
                  <span className="value formatted-text">{selectedInitiative.proposed_workflow}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Inputs & Data Sources:</span>
                  <span className="value formatted-text">{selectedInitiative.inputs_and_data_sources}</span>
                </div>
              </div>

              {(selectedInitiative.status === 'pending' || selectedInitiative.status === 'parked') && (
                <>
                <div style={{ marginBottom: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '6px', border: '1px solid #ddd' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>Notification Preferences</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="prefNotifySubmitter"
                        checked={notifyOnApprove}
                        onChange={(e) => setNotifyOnApprove(e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <label htmlFor="prefNotifySubmitter" style={{ margin: 0, fontWeight: 'normal', cursor: 'pointer', fontSize: '13px' }}>
                        Notify submitter
                      </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        id="prefNotifyApprovers"
                        checked={notifyApprovers}
                        onChange={(e) => setNotifyApprovers(e.target.checked)}
                        disabled={isSubmitting}
                      />
                      <label htmlFor="prefNotifyApprovers" style={{ margin: 0, fontWeight: 'normal', cursor: 'pointer', fontSize: '13px' }}>
                        Notify approvers
                      </label>
                    </div>
                  </div>
                </div>
                <div className="action-buttons">
                  <button
                    className="btn btn-success"
                    onClick={() => setAction('approve')}
                    disabled={isSubmitting || (selectedInitiative.division || 'Unassigned') !== division}
                    title={(selectedInitiative.division || 'Unassigned') !== division ? 'Can only approve initiatives from your division' : ''}
                  >
                    Approve
                  </button>
                  {selectedInitiative.status === 'pending' && (
                    <button
                      className="btn btn-park"
                      onClick={() => setAction('park')}
                      disabled={isSubmitting || (selectedInitiative.division || 'Unassigned') !== division}
                      title={(selectedInitiative.division || 'Unassigned') !== division ? 'Can only manage initiatives from your division' : ''}
                    >
                      ⏸ Park
                    </button>
                  )}
                  <button
                    className="btn btn-danger"
                    onClick={() => setAction('reject')}
                    disabled={isSubmitting || (selectedInitiative.division || 'Unassigned') !== division}
                    title={(selectedInitiative.division || 'Unassigned') !== division ? 'Can only manage initiatives from your division' : ''}
                  >
                    Reject
                  </button>
                  {selectedInitiative.status === 'pending' && (
                    <button
                      className="btn btn-primary"
                      onClick={() => setAction('delegate')}
                      disabled={isSubmitting || (selectedInitiative.division || 'Unassigned') !== division}
                      title={(selectedInitiative.division || 'Unassigned') !== division ? 'Can only manage initiatives from your division' : ''}
                    >
                      🔄 Delegate to Reviewer
                    </button>
                  )}
                  <button
                    className="btn btn-info"
                    onClick={handleResendNotification}
                    disabled={isSubmitting}
                    title="Resend notification email"
                  >
                    ✉️ Resend Notification
                  </button>
                </div>
                {(() => {
                  const div = selectedInitiative.division || null;
                  const dqForDiv = dqReviewers.filter(r => r.division === div);
                  return (
                    <div style={{ marginTop: '12px', padding: '8px 14px', background: '#f0f7ff', border: '1px solid #cce0ff', borderRadius: '6px', fontSize: '12px' }}>
                      <strong>DQ Reviewer{dqForDiv.length !== 1 ? 's' : ''} ({div || '—'}):</strong>{' '}
                      <span style={{ color: dqForDiv.length === 0 ? '#888' : '#333' }}>
                        {dqForDiv.length === 0
                          ? 'None assigned'
                          : dqForDiv.map(r => r.display_name ? `${r.display_name} (${r.email})` : r.email).join(', ')}
                      </span>
                    </div>
                  );
                })()}
                </>
              )}


              {selectedInitiative.status !== 'pending' && selectedInitiative.status !== 'parked' && (
                <div className="status-info">
                  <p>This initiative has already been {selectedInitiative.status.toLowerCase()}.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteInitiativeId && (
        <div className="modal-overlay" onClick={() => !isSubmitting && setShowDeleteConfirm(false)}>
          <div className="modal-content" style={{ maxWidth: '400px', maxHeight: '200px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '12px 16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Confirm Delete</h3>
              <button className="modal-close" onClick={() => !isSubmitting && setShowDeleteConfirm(false)} disabled={isSubmitting} style={{ width: '28px', height: '28px', fontSize: '20px' }}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '12px 16px' }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '14px', color: '#333', margin: '0 0 12px 0', lineHeight: '1.4' }}>
                  Are you sure you want to delete initiative #{deleteInitiativeId}?
                </p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button
                    className="btn btn-danger"
                    onClick={confirmDelete}
                    disabled={isSubmitting}
                    style={{ flex: 1, maxWidth: '120px', padding: '8px 12px', fontSize: '13px' }}
                  >
                    {isSubmitting ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isSubmitting}
                    style={{ flex: 1, maxWidth: '120px', padding: '8px 12px', fontSize: '13px' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Separate Modal for Approval/Rejection/Delegation Action */}
      {action && selectedInitiative && (
        <div className="modal-overlay" onClick={() => { setAction(null); setComment(''); setSelectedReviewerEmail(''); setNotifyOnApprove(true); setNotifyApprovers(true); }}>
          <div className="modal-content action-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{action === 'approve' ? 'Approve Initiative' : action === 'reject' ? 'Reject Initiative' : action === 'park' ? 'Park Initiative' : 'Delegate to Reviewer'}</h3>
              <button className="modal-close" onClick={() => { setAction(null); setComment(''); setSelectedReviewerEmail(''); setNotifyOnApprove(true); setNotifyApprovers(true); }}>×</button>
            </div>

            <div className="modal-body">
              {message && (
                <div className={`message-alert ${message.type}`}>
                  {message.text}
                </div>
              )}

              {action === 'delegate' ? (
                <div className="delegation-section">
                  <div className="form-group">
                    <label>
                      Reviewer Email <span className="required">*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="email"
                        value={selectedReviewerEmail}
                        onChange={(e) => handleReviewerEmailChange(e.target.value)}
                        onFocus={() => {
                          // Show all active delegates when input is focused and empty
                          if (!selectedReviewerEmail) {
                            const suggestions = delegates
                              .filter(d => d.is_active)
                              .map(delegate => ({
                                email: delegate.email,
                                name: delegate.display_name || delegate.email
                              }));
                            setFilteredSuggestions(suggestions);
                            setShowSuggestions(suggestions.length > 0);
                          } else {
                            setShowSuggestions(true);
                          }
                        }}
                        placeholder="Type delegate name or email..."
                        disabled={isSubmitting}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          border: emailValidationError ? '2px solid #dc3545' : '1px solid #ccc',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                      {showSuggestions && filteredSuggestions.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: '#fff',
                          border: '1px solid #ccc',
                          borderTop: 'none',
                          borderRadius: '0 0 4px 4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 10,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {filteredSuggestions.map((suggestion: any) => (
                            <div
                              key={suggestion.email}
                              onClick={() => selectReviewerFromSuggestion(suggestion.email, suggestion.name)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                backgroundColor: selectedReviewerEmail === suggestion.email ? '#f0f0f0' : '#fff',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = selectedReviewerEmail === suggestion.email ? '#f0f0f0' : '#fff')}
                            >
                              <div style={{ fontWeight: 500 }}>
                                {suggestion.name}
                              </div>
                              <div style={{ fontSize: '12px', color: '#666' }}>{suggestion.email}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {emailValidationError && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', fontWeight: 500 }}>
                        ⚠️ {emailValidationError}
                      </div>
                    )}
                    {selectedReviewerEmail && !emailValidationError && !reviewers.some(r => r.email === selectedReviewerEmail) && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        ℹ️ Custom email: {selectedReviewerEmail}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Additional Comment (optional)</label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Add any specific instructions for the reviewer..."
                      disabled={isSubmitting}
                      rows={4}
                    />
                  </div>

                  <div style={{ marginTop: '16px' }}>
                    <label>
                      Division
                    </label>
                    <select
                      className="division-inline-select"
                      value={editingDivision}
                      onChange={(e) => {
                        setEditingDivision(e.target.value);
                        saveDivision(selectedInitiative.id, e.target.value);
                      }}
                      disabled={divisionSaving || isSubmitting || (selectedInitiative.division || 'Unassigned') !== division}
                      style={{ width: '100%' }}
                    >
                      <option value="">— Not set —</option>
                      {Array.from(new Set([
                        ...getDivisions(),
                        ...(editingDivision ? [editingDivision] : [])
                      ])).sort().map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>

                  {/* Delegate Actions */}
                  <div className="comment-actions">
                    <button
                      className="btn btn-primary"
                      onClick={handleDelegate}
                      disabled={isSubmitting || !selectedReviewerEmail || !!emailValidationError}
                    >
                      {isSubmitting ? 'Delegating...' : 'Delegate'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setAction(null);
                        setComment('');
                        setSelectedReviewerEmail('');
                        setNotifyOnApprove(true);
                        setNotifyApprovers(true);
                      }}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="comment-section">
                  {action === 'park' && (
                    <div className="park-info-banner">
                      ⏸ Parking puts this request on hold without approving or rejecting it. SLA will be marked as N/A. The requestor and all reviewers will be notified.
                    </div>
                  )}
                  <label>
                    {action === 'approve' ? 'Approval' : action === 'park' ? 'Park' : 'Rejection'} Comment <span className="required">*</span>
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={action === 'approve' ? 'Enter approval comment...' : action === 'park' ? 'Enter reason for parking...' : 'Enter rejection reason...'}
                    disabled={isSubmitting}
                    rows={5}
                  />
                  <div className="comment-actions">
                    <button
                      className={`btn ${action === 'approve' ? 'btn-success' : action === 'park' ? 'btn-park' : 'btn-danger'}`}
                      onClick={action === 'approve' ? handleApprove : action === 'park' ? handlePark : handleReject}
                      disabled={isSubmitting || !comment.trim()}
                    >
                      {isSubmitting ? 'Processing...' : action === 'approve' ? 'Confirm Approval' : action === 'park' ? 'Confirm Park' : 'Confirm Rejection'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setAction(null);
                        setComment('');
                        setNotifyOnApprove(true);
                        setNotifyApprovers(true);
                      }}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Workflow Modal */}
      {showWorkflowModal && (
        <div className="modal-overlay" onClick={() => !workflowLoading && setShowWorkflowModal(false)}>
          <div className="modal-content workflow-modal" style={{ maxWidth: '1000px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📊 Workflow</h3>
              <button className="modal-close" onClick={() => !workflowLoading && setShowWorkflowModal(false)} disabled={workflowLoading}>×</button>
            </div>
            <div className="modal-body">
              {workflowLoading ? (
                <div className="workflow-loading">
                  <div className="spinner"></div>
                  <p>Loading delegation flow...</p>
                </div>
              ) : delegationFlow && (delegationFlow.flow?.length > 0 || delegationFlow.current_reviewer_email || delegationFlow.approval_status) ? (
                <div className="workflow-container">
                  {/* Show approval/rejection status if available */}
                  {delegationFlow.approval_status ? (
                    <>
                      {/* Show submission and approval timeline info as horizontal flow */}
                      <div style={{
                        marginTop: '15px',
                        display: 'flex',
                        gap: '15px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                        overflowX: 'auto'
                      }}>
                        <div style={{
                          padding: '16px 20px',
                          backgroundColor: '#e3f2fd',
                          borderLeft: '4px solid #2196f3',
                          borderRadius: '4px',
                          fontSize: '14px',
                          minWidth: '220px'
                        }}>
                          <div style={{ color: '#1565c0', fontWeight: '600', marginBottom: '6px', fontSize: '15px' }}>Request Submitted</div>
                          <div style={{ color: '#666', fontSize: '13px', marginBottom: '8px' }}>
                            {delegationFlow?.submitted_by_name || delegationFlow?.submitted_by || 'N/A'}
                          </div>
                          <div style={{ color: '#333', fontSize: '13px' }}>
                            {delegationFlow?.submitted_date ? formatDateWithTime(delegationFlow.submitted_date) : 'N/A'}
                          </div>
                        </div>
                        <div style={{ color: '#999', fontSize: '20px', fontWeight: '300' }}>→</div>
                        <div style={{
                          padding: '16px 20px',
                          backgroundColor: delegationFlow.approval_status === 'approved' ? '#e8f5e9' : '#ffebee',
                          borderLeft: `4px solid ${delegationFlow.approval_status === 'approved' ? '#4caf50' : '#f44336'}`,
                          borderRadius: '4px',
                          fontSize: '14px',
                          minWidth: '220px'
                        }}>
                          <div style={{ color: delegationFlow.approval_status === 'approved' ? '#2e7d32' : '#ee0000', fontWeight: '600', marginBottom: '6px', fontSize: '15px' }}>
                            {delegationFlow.approval_status === 'approved' ? 'Approved' : 'Rejected'}
                          </div>
                          <div style={{ color: '#666', fontSize: '13px', marginBottom: '8px' }}>
                            {delegationFlow.approved_by || 'N/A'}
                          </div>
                          <div style={{ color: '#333', fontSize: '13px' }}>
                            {delegationFlow.approval_date ? formatDateWithTime(delegationFlow.approval_date) : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : delegationFlow.flow && delegationFlow.flow.length > 0 ? (
                    <div className="horizontal-timeline">
                      {/* Show initial requestor */}
                      <div className="timeline-box" style={{ backgroundColor: '#fff3e0', borderColor: '#ff9800', width: '140px' }}>
                        <div className="box-email" style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Initiated By</div>
                        <div className="box-email" style={{ fontSize: '13px', marginBottom: '4px' }}>{delegationFlow?.submitted_by_name || delegationFlow?.submitted_by || 'N/A'}</div>
                        <div className="box-time">{delegationFlow?.submitted_date ? formatDateWithTime(delegationFlow.submitted_date) : 'N/A'}</div>
                      </div>
                      <div className="timeline-arrow">→</div>
                      {delegationFlow.flow.map((step: any, index: number) => (
                        <React.Fragment key={step.step}>
                          <div className="timeline-box" style={{ width: '140px' }}>
                            <div className="box-email" style={{ fontSize: '13px', marginBottom: '4px' }}>From: {step.from_name || step.from_email}</div>
                            <div className="box-time" style={{ fontSize: '12px', marginBottom: '6px' }}>↓</div>
                            <div className="box-email" style={{ fontSize: '13px', fontWeight: '600' }}>To: {step.to_name || step.to_email}</div>
                            <div className="box-time" style={{ fontSize: '12px', marginTop: '4px' }}>{formatRelativeTime(step.delegated_date)}</div>
                          </div>
                          {index < delegationFlow.flow.length - 1 && (
                            <div className="timeline-arrow">→</div>
                          )}
                        </React.Fragment>
                      ))}
                      {delegationFlow.current_reviewer_email && (
                        <>
                          <div className="timeline-arrow">→</div>
                          <div className="timeline-box current" style={{ backgroundColor: '#e3f2fd', width: '140px' }}>
                            <div className="box-email" style={{ fontSize: '13px', fontWeight: '600' }}>Current Reviewer</div>
                            <div className="box-email" style={{ fontSize: '13px', marginTop: '4px' }}>{delegationFlow.current_reviewer_name || delegationFlow.current_reviewer_email}</div>
                            <div className="box-time">now</div>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="horizontal-timeline">
                      <div className="timeline-box current" style={{ width: '140px' }}>
                        <div className="box-email">{delegationFlow.current_reviewer_email}</div>
                        <div className="box-time">Reviewing</div>
                      </div>
                    </div>
                  )}

                  <div className="workflow-summary">
                  </div>
                </div>
              ) : (
                <div className="no-workflow">
                  <p>No workflow history found</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowWorkflowModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InitiativeManagement;
